import React, { useEffect, useState, Suspense, useContext } from "react";
import "./App.css";
import "./pages/shared/shared.css";
import "./components/desktop/Navbar/Navbar.css";
import { checkout, various } from "./Data/siteContent";
import Home_Mobile from "./pages/Home_Mobile";
import { CartProvider } from "./Contexts/CartContext";
import { Routes, Route, useNavigate } from "react-router-dom";
import Checkout_Desktop from "./pages/Checkout_Desktop";
import OutOfStock from "./pages/shared/OutOfStock";
import { useLocation } from "react-router-dom";
import Checkout_Mobile from "./pages/Checkout_Mobile";
import LoadingScreen from "./components/shared/LoadingBar";
import TopBar from "./components/desktop/TopBar";
import { CartContext } from "./Contexts/CartContext";
import { AvailabilityContext } from "./Contexts/AvailabilityContext";
import PrivacyPolicy from "./pages/shared/PrivacyPolicy";
import SettingsEditor from "./pages/shared/SettingsEditor";
import BlackwaveEditor from "./pages/shared/BlackwaveEditor";
import Updates from "./pages/shared/Updates";
import TermsConditions from "./pages/shared/TermsConditions";
import { Navigate } from "react-router-dom";
import CookieConsentBlock from "./components/shared/CookieConsentBlock";
import MaintenancePage from "./pages/shared/MaintenancePage";
import OrderConfirmation from "./pages/shared/OrderConfirmation";
import Unsubscribe from "./pages/shared/Unsubscribe";
import getProducts from "./Data/Products";

const About_Desktop = React.lazy(() => import("./pages/About_Desktop"));
const Home_Desktop = React.lazy(() => import("./pages/Home_Desktop"));

const EXTRA_FEATURE_COMPONENTS = {
  "settings-editor": SettingsEditor,
  "blackwave-editor": BlackwaveEditor,
  "firmware-updates": Updates,
};

function App() {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isProductOpen, setIsProductOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("products");
  const [loadingComplete, setLoadingComplete] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has("success") || params.has("noloader");
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [isSuccessHandled, setIsSuccessHandled] = useState(false);
  const [cookiesHandled, setCookiesHandled] = useState(() => {
    return localStorage.getItem("cookiesAccepted") === "true";
  });

  const handleAcceptCookies = () => {
    localStorage.setItem("cookiesAccepted", "true");
    setCookiesHandled(true);
  };

  const handleDeclineCookies = () => {
    localStorage.setItem("cookiesAccepted", "true");
    setCookiesHandled(true);
  };

  useEffect(() => {
    if (windowWidth < 1025) {
      if (!cookiesHandled) {
        document.body.classList.add("no-scroll");
      } else {
        document.body.classList.remove("no-scroll");
      }
      return () => {
        document.body.classList.remove("no-scroll");
      };
    }
  }, [windowWidth, cookiesHandled]);

  const { clearCart } = useContext(CartContext);
  const { maintenance, loading: initLoading } = useContext(AvailabilityContext);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const success = queryParams.get("success");

    if (success === "true" && !isSuccessHandled) {
      clearCart();
      setIsSuccessHandled(true);
      navigate("/order-confirmation", { replace: true });
    } else if (success === "false") {
      setShowFailedModal(true);
      const newUrl = window.location.pathname;
      window.history.replaceState(null, "", newUrl);
    }
  }, [location, clearCart, isSuccessHandled, navigate]);

  const closeFailedModal = () => {
    setShowFailedModal(false);
  };

  useEffect(() => {
    if (loadingComplete && windowWidth < 1025) {
      window.scrollTo(0, 0);
    }
  }, [loadingComplete, windowWidth]);

  useEffect(() => {
    if (
      location.pathname === "/privacy-policy" ||
      location.pathname === "/terms-conditions" ||
      location.pathname === "/order-confirmation"
    ) {
      document.body.style.borderBottom = "none";
    } else {
      document.body.style.borderBottom = "";
    }
  }, [location.pathname]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    let isResizing = false;
    let activeLeft, activeRight, activeResizerEl;

    const container = document.querySelector(".container-sketch");
    const resizerProducts = document.querySelector(".resizer-products");
    const resizerAbout = document.querySelector(".resizer-about");

    function resize(e) {
      if (!isResizing || !container) return;
      const containerOffsetLeft = container.getBoundingClientRect().left;
      const containerWidth = container.offsetWidth;
      const newLeftWidth = e.clientX - containerOffsetLeft;
      const newRightWidth = containerWidth - newLeftWidth;
      activeLeft.style.width = newLeftWidth + "px";
      activeRight.style.width = newRightWidth + "px";
      activeResizerEl.style.left = newLeftWidth - activeResizerEl.offsetWidth / 2 + "px";
    }

    function stopResize() {
      isResizing = false;
      document.body.classList.remove("disable-select");
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", stopResize);
    }

    function setupResizer(resizer, left, right) {
      if (!resizer) return;
      const handler = e => {
        e.preventDefault();
        isResizing = true;
        activeLeft = left;
        activeRight = right;
        activeResizerEl = resizer;
        document.body.classList.add("disable-select");
        document.addEventListener("mousemove", resize);
        document.addEventListener("mouseup", stopResize);
      };
      resizer.addEventListener("mousedown", handler);
      return () => resizer.removeEventListener("mousedown", handler);
    }

    const cleanupProducts = setupResizer(
      resizerProducts,
      document.querySelector(".marquee-30-products"),
      document.querySelector(".marquee-70-products")
    );
    const cleanupAbout = setupResizer(
      resizerAbout,
      document.querySelector(".marquee-70-about"),
      document.querySelector(".marquee-30-about")
    );

    return () => {
      cleanupProducts?.();
      cleanupAbout?.();
    };
  }, [loadingComplete]);

  const handleLogoClick = () => {
    setCurrentPage("about");
  };

  if (!loadingComplete) {
    return <LoadingScreen onComplete={() => setLoadingComplete(true)} />;
  }

  const hasPaymentReturn =
    new URLSearchParams(window.location.search).has("success") ||
    location.pathname === "/order-confirmation" ||
    showFailedModal;
  if (!initLoading && maintenance && !hasPaymentReturn) {
    return <MaintenancePage />;
  }

  const isMobile = windowWidth < 1025;

  const products = getProducts();
  const extraFeatureRoutes = Object.values(products).flatMap(product =>
    (product.extraFeatures || []).map(feature => ({
      path: `/products/${product.id}/${feature.routeSlug}`,
      productId: product.id,
      feature,
      Component: EXTRA_FEATURE_COMPONENTS[feature.type],
    }))
  );

  const failedModal = showFailedModal && (
    <div className="app-modal-overlay">
      <div className="app-modal-success app-modal-failed">
        <h2>{checkout.paymentFailed}</h2>
        <p>{checkout.errorDuringPayment}</p>
        <button onClick={closeFailedModal}>{various.close}</button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <CartProvider>
        {!cookiesHandled && <div className="background-overlay" />}
        <CookieConsentBlock isMobile onAccept={handleAcceptCookies} onDecline={handleDeclineCookies} />
        {failedModal}
        <Routes>
          <Route path="/" element={<Home_Mobile />} />
          <Route path="/products" element={<Home_Mobile />} />
          <Route path="/about" element={<Home_Mobile />} />
          <Route path="/products/:productName" element={<Home_Mobile />} />
          <Route path="/checkout" element={<Checkout_Mobile />} />
          <Route path="/order-confirmation" element={<OrderConfirmation />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          {extraFeatureRoutes.map(({ path, feature, productId, Component }) => (
            <Route key={path} path={path} element={<Component feature={feature} productId={productId} />} />
          ))}
          <Route path="/out-of-stock" element={<OutOfStock />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
        </Routes>
      </CartProvider>
    );
  }

  return (
    <CartProvider>
      {!cookiesHandled && <div className="background-overlay" />}
      <CookieConsentBlock isMobile={false} onAccept={handleAcceptCookies} onDecline={handleDeclineCookies} />
      <TopBar handleLogoClick={handleLogoClick} handlePageChange={handlePageChange} currentPage={currentPage} />
      {failedModal}
      <Routes>
        <Route path="/checkout" element={<Checkout_Desktop />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-conditions" element={<TermsConditions />} />

        {extraFeatureRoutes.map(({ path, feature, productId, Component }) => (
          <Route key={path} path={path} element={<Component feature={feature} productId={productId} />} />
        ))}

        <Route path="/out-of-stock" element={<OutOfStock />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />

        <Route
          path="/about"
          element={
            <div className="App">
              <Suspense fallback={<div className="loading-all-app">{various.loading}</div>}>
                <About_Desktop />
              </Suspense>
            </div>
          }
        />

        <Route
          path="/products"
          element={
            <div className="App">
              <Suspense fallback={<div className="loading-all-app">{various.loading}</div>}>
                <Home_Desktop isProductOpen={isProductOpen} setIsProductOpen={setIsProductOpen} />
              </Suspense>
            </div>
          }
        />

        <Route
          path="/products/:productName"
          element={
            <div className="App">
              <Suspense fallback={<div className="loading-all-app">{various.loading}</div>}>
                <Home_Desktop isProductOpen={isProductOpen} setIsProductOpen={setIsProductOpen} />
              </Suspense>
            </div>
          }
        />

        <Route path="/" element={<Navigate to="/products" />} />
      </Routes>
    </CartProvider>
  );
}

export default App;
